/**
 * Per-integration config objects for the generic IntegrationSyncWorkflow.
 *
 * Adding a new task-sync integration means adding one entry here and
 * registering it in MembraneIntegration.vue.
 */

import type { MembraneClient } from "@membranehq/sdk";
import { markRaw } from "vue";

import type { Cell } from "@ctrl/controller-rpc";

import { fetchLabelForConnection } from "@/components/integrations/useIntegrations";
import {
  arrayProperty,
  isRecord,
  optionalStringProperty,
  property,
} from "@/utils/unknown";

import GmailIllustration from "./GmailIllustration.vue";
import OutlookIllustration from "./OutlookIllustration.vue";
import TaskListIllustration from "./TaskListIllustration.vue";
import type {
  FieldNames,
  StatusCellMapping,
  StatusCellResolution,
} from "./useDataCollectionBinding";
import {
  getStringCellParam,
  type FilterItem,
  type IntegrationSyncConfig,
  type TaskSyncMapping,
  type TaskSyncMappingProperty,
} from "./useIntegrationSyncWorkflow";

const stringCell = (value: string): Cell => ({ type: "string", value });

function getDataCollectionItems(
  response: unknown,
  nameFields: string[]
): FilterItem[] {
  const items: FilterItem[] = [];
  for (const record of arrayProperty(response, "records") ?? []) {
    const id = optionalStringProperty(record, "id");
    if (!id) continue;
    const fields = property(record, "fields");
    let name: string | undefined;
    for (const field of nameFields) {
      const value = optionalStringProperty(fields, field);
      if (value) {
        name = value;
        break;
      }
    }
    items.push({ id, name: name ?? id });
  }
  return items;
}

export function getGitHubRepositoryItems(response: unknown): FilterItem[] {
  const items: FilterItem[] = [];
  for (const record of arrayProperty(response, "records") ?? []) {
    const fields = property(record, "fields");
    const fullName = optionalStringProperty(fields, "full_name");
    if (fullName) items.push({ id: fullName, name: fullName });
  }
  return items;
}

export function getGitHubIssueParameters(
  choice: "optionA" | "optionB" | "optionC",
  repositoryFullName: string
): Record<string, string> {
  if (choice !== "optionB") return {};
  const separator = repositoryFullName.indexOf("/");
  if (separator <= 0 || separator === repositoryFullName.length - 1) return {};
  return {
    owner: repositoryFullName.slice(0, separator),
    repo: repositoryFullName.slice(separator + 1),
  };
}

function getAsanaWorkspaceIds(response: unknown): string[] {
  const ids: string[] = [];
  const data = property(response, "data");
  for (const workspace of arrayProperty(data, "workspaces") ?? []) {
    const id = optionalStringProperty(workspace, "gid");
    if (id) ids.push(id);
  }
  return ids;
}

function getAsanaProjects(response: unknown): FilterItem[] {
  const projects: FilterItem[] = [];
  for (const project of arrayProperty(response, "data") ?? []) {
    const id = optionalStringProperty(project, "gid");
    const name = optionalStringProperty(project, "name");
    if (id && name) projects.push({ id, name });
  }
  return projects;
}

function getOutlookCategories(response: unknown): FilterItem[] {
  const categories: FilterItem[] = [];
  for (const category of arrayProperty(response, "value") ?? []) {
    const name = optionalStringProperty(category, "displayName");
    if (name) categories.push({ id: name, name });
  }
  return categories;
}

type ClickUpProxyGet = (path: string) => Promise<unknown>;

const clickUpResources = (
  response: unknown,
  key: string
): Array<{ id: string; name: string }> => {
  const values = property(response, key);
  if (!Array.isArray(values)) return [];
  return values.flatMap((value) => {
    const rawId = property(value, "id");
    if (typeof rawId !== "string" && typeof rawId !== "number") return [];
    const id = String(rawId);
    const rawName = property(value, "name");
    return [{ id, name: typeof rawName === "string" ? rawName : id }];
  });
};

export function clickUpListDisplayName(
  fields: Record<string, string | undefined> | undefined,
  fallbackId: string
): string {
  const listName = fields?.["name"] ?? fallbackId;
  return [
    fields?.["workspace_name"],
    fields?.["space_name"],
    fields?.["folder_name"],
    listName,
  ]
    .filter((part): part is string => !!part)
    .join(" / ");
}

export async function fetchClickUpListItems(
  get: ClickUpProxyGet
): Promise<FilterItem[]> {
  const records: FilterItem[] = [];
  const workspaces = clickUpResources(await get("/team"), "teams");

  for (const workspace of workspaces) {
    const spaces = clickUpResources(
      await get(
        `/team/${encodeURIComponent(workspace.id)}/space?archived=false`
      ),
      "spaces"
    );

    for (const space of spaces) {
      const [foldersResponse, folderlessResponse] = await Promise.all([
        get(`/space/${encodeURIComponent(space.id)}/folder?archived=false`),
        get(`/space/${encodeURIComponent(space.id)}/list?archived=false`),
      ]);

      const folderlessLists = clickUpResources(folderlessResponse, "lists");
      records.push(
        ...folderlessLists.map((list) => ({
          id: list.id,
          name: clickUpListDisplayName(
            {
              name: list.name,
              workspace_name: workspace.name,
              space_name: space.name,
            },
            list.id
          ),
        }))
      );

      for (const folder of clickUpResources(foldersResponse, "folders")) {
        const lists = clickUpResources(
          await get(
            `/folder/${encodeURIComponent(folder.id)}/list?archived=false`
          ),
          "lists"
        );
        records.push(
          ...lists.map((list) => ({
            id: list.id,
            name: clickUpListDisplayName(
              {
                name: list.name,
                workspace_name: workspace.name,
                space_name: space.name,
                folder_name: folder.name,
              },
              list.id
            ),
          }))
        );
      }
    }
  }

  return records;
}

/**
 * The standard display name for an integration sync database:
 * "SERVICE_NAME (LABEL)" when the connection label (typically the account
 * email) is known, the bare service name otherwise. The filter name (project,
 * label, list, …) is appended when present so that several syncs of the same
 * account produce distinguishable database names.
 */
const getGenericDisplayName = (
  serviceName: string,
  connectionLabel?: string,
  filterName?: string
): string => {
  const base = connectionLabel
    ? `${serviceName} (${connectionLabel})`
    : serviceName;
  return filterName ? `${base} - ${filterName}` : base;
};

/** Live name of a filter item, falling back to its raw id. */
const filterItemName = (filterItems: FilterItem[], id: string): string =>
  filterItems.find((item) => item.id === id)?.name ?? id;

// ── Gmail ───────────────────────────────────────────────────────────────────

export const gmailConfig: IntegrationSyncConfig = {
  integrationKey: "gmail",
  label: "Gmail",
  description:
    "Connect your Gmail account to get your emails as tasks in Routine.",
  collectionKey: "emails-task-bindings",

  filterOptionA: { icon: "⭐", label: "Starred emails" },
  filterOptionB: { icon: "🏷", label: "Specific label" },
  configureQuestion: "Which emails should become tasks?",

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("labels")
      .list();
    return getDataCollectionItems(response, ["name"]);
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "gmail"),

  getParameters: (_choice, filterId) => ({
    labelId: _choice === "optionA" ? "STARRED" : filterId,
  }),

  getChoiceFromParameters: (parameters) => {
    const labelId = getStringCellParam(parameters, "labelId");
    return !labelId || labelId === "STARRED"
      ? { choice: "optionA", filterId: "" }
      : { choice: "optionB", filterId: labelId };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Gmail", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const labelId = getStringCellParam(parameters, "labelId");
    return !labelId || labelId === "STARRED"
      ? "Starred emails"
      : labelId === "INBOX"
        ? "Inbox emails"
        : `"${filterItemName(filterItems, labelId)}" emails`;
  },

  getHint: (parameters, filterItems, storedName) => {
    const labelId = getStringCellParam(parameters, "labelId");
    if (!labelId || labelId === "STARRED") {
      return "Starring an email will automatically create a task in your Routine inbox.";
    }
    if (labelId === "INBOX") {
      return "Emails arriving in your inbox will automatically become tasks in your Routine inbox.";
    }
    const liveName = filterItems.find((l) => l.id === labelId)?.name;
    return `Emails labelled "${liveName ?? storedName ?? labelId}" will automatically become tasks in your Routine inbox.`;
  },

  fields: { url: "url", completed: "archived" },

  completionActions: [
    {
      label: "Archive the email",
      fields: { url: "url", completed: "archived" },
    },
    {
      label: "Unstar the email",
      visibleFor: (parameters) => {
        const labelId = getStringCellParam(parameters, "labelId");
        return !labelId || labelId === "STARRED";
      },
      fields: {
        url: "url",
        completed: "starred",
        customStatusCells: {
          done: {
            read: [{ type: "bool", value: false }],
            write: { type: "bool", value: false },
          },
          todo: {
            read: [{ type: "bool", value: true }],
            write: { type: "bool", value: true },
          },
          discarded: {
            read: [],
            write: { type: "bool", value: false },
          },
        },
      },
    },
  ],

  IllustrationComponent: markRaw(GmailIllustration),
};

// ── Todoist ─────────────────────────────────────────────────────────────────

export const todoistConfig: IntegrationSyncConfig = {
  integrationKey: "todoist",
  label: "Todoist",
  description: "Connect your Todoist account to get your tasks in Routine.",
  collectionKey: "tasks",

  filterOptionA: { icon: "✅", label: "All tasks" },
  filterOptionB: { icon: "📁", label: "Specific project" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("projects")
      .list();
    return getDataCollectionItems(response, ["name", "content"]);
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "todoist"),

  getParameters: (choice, filterId): Record<string, string> =>
    choice === "optionB" && filterId ? { projectId: filterId } : {},

  getChoiceFromParameters: (parameters) => {
    const projectId = getStringCellParam(parameters, "projectId");
    return projectId
      ? { choice: "optionB", filterId: projectId }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Todoist", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const pid = getStringCellParam(parameters, "projectId");
    return pid ? `${filterItemName(filterItems, pid)} tasks` : "All tasks";
  },

  getHint: (parameters, filterItems, storedName) => {
    const pid = getStringCellParam(parameters, "projectId");
    if (!pid) {
      return "All your Todoist tasks will automatically appear in your Routine inbox.";
    }
    const liveName = filterItems.find((p) => p.id === pid)?.name;
    return `Tasks in project "${liveName ?? storedName ?? pid}" will automatically appear in your Routine inbox.`;
  },

  fields: {
    url: "url",
    scheduled: "routine_date",
    completed: "checked",
  },

  completionActions: [
    {
      label: "Complete the task in Todoist",
      fields: { url: "url", scheduled: "routine_date", completed: "checked" },
    },
    {
      label: "Delete the task",
      fields: {
        url: "url",
        scheduled: "routine_date",
        completed: "is_deleted",
      },
    },
  ],

  illustrationRows: [
    { name: "Buy groceries", project: "Personal", completed: true },
    { name: "Review pull request #42", project: "Work", highlight: true },
    { name: "Send weekly update", project: "Work" },
    { name: "Book dentist appointment", project: "Personal" },
    { name: "Plan team offsite", project: "Work" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── TickTick ────────────────────────────────────────────────────────────────

export const tickTickConfig: IntegrationSyncConfig = {
  integrationKey: "ticktick",
  label: "TickTick",
  description: "Connect your TickTick account to get your tasks in Routine.",
  collectionKey: "tasks",

  // A project is mandatory. TickTick's only cross-project task endpoint
  // (POST /task/filter) caps at 200 tasks and exposes no cursor, so an
  // unscoped sync would silently truncate; and POST /task rejects a create
  // without a projectId, so an unscoped sync could not push tasks back either.
  requiresFilter: true,
  configureQuestion: "Which list should sync to Routine?",

  // filterOptionA is unused when requiresFilter is set (no "all" card is
  // shown); filterOptionB drives the single required list picker.
  filterOptionA: { icon: "✅", label: "All tasks" },
  filterOptionB: { icon: "📋", label: "Specific list" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("projects")
      .list();
    return getDataCollectionItems(response, ["name"]);
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "ticktick"),

  // A list is always required (see requiresFilter), so the parameters always
  // carry the selected projectId — never an empty map.
  getParameters: (_choice, filterId): Record<string, string> => ({
    projectId: filterId,
  }),

  getChoiceFromParameters: (parameters) => {
    const projectId = getStringCellParam(parameters, "projectId");
    return projectId
      ? { choice: "optionB", filterId: projectId }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("TickTick", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const pid = getStringCellParam(parameters, "projectId");
    return pid ? `${filterItemName(filterItems, pid)} tasks` : "All tasks";
  },

  // TickTick does not let other apps reopen a completed task, so completion is
  // one-way. Saying so here means users meet the limitation while setting the
  // sync up, rather than as a failure the first time they untick a task.
  getHint: (parameters, filterItems, storedName) => {
    const oneWay =
      " Completing a task in Routine completes it in TickTick, but reopening " +
      "one only affects Routine — TickTick does not allow other apps to " +
      "reopen a completed task.";
    const pid = getStringCellParam(parameters, "projectId");
    if (!pid) {
      return (
        "All your TickTick tasks will automatically appear in your Routine inbox." +
        oneWay
      );
    }
    const liveName = filterItems.find((p) => p.id === pid)?.name;
    return (
      `Tasks in list "${liveName ?? storedName ?? pid}" will automatically appear in your Routine inbox.` +
      oneWay
    );
  },

  // `routine_date`, `routine_completed` and `url` are all synthesised by the
  // connector — TickTick returns none of them. `status` is deliberately not
  // bound: it is absent from TickTick's Update Task body, so completion has to
  // go through the dedicated complete endpoint, which the connector does when
  // `routine_completed` is written.
  fields: {
    url: "url",
    scheduled: "routine_date",
    completed: "routine_completed",
  },

  // No `completionActions`: TickTick's Open API can only close a task. It has
  // no re-open endpoint and no soft-delete field to offer as an alternative.

  illustrationRows: [
    { name: "Submit expense report", project: "Work", completed: true },
    { name: "Draft sprint retrospective", project: "Work", highlight: true },
    { name: "Renew gym membership", project: "Personal" },
    { name: "Water the plants", project: "Personal" },
    { name: "Prepare 1:1 agenda", project: "Work" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── Asana ───────────────────────────────────────────────────────────────────

export const asanaConfig: IntegrationSyncConfig = {
  integrationKey: "asana",
  label: "Asana",
  description: "Connect your Asana account to get your tasks in Routine.",
  collectionKey: "tasks",

  filterOptionA: { icon: "👤", label: "My Tasks" },
  filterOptionB: { icon: "📋", label: "Specific project" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    // Asana requires a workspace filter — resolve workspaces first, then
    // fetch projects for each workspace.
    const me: unknown = await membrane
      .connection(connId)
      .proxy.get("/users/me?opt_fields=workspaces");
    const workspaceIds = getAsanaWorkspaceIds(me);

    const collected: FilterItem[] = [];
    for (const workspaceId of workspaceIds) {
      const page: unknown = await membrane
        .connection(connId)
        .proxy.get(
          `/projects?workspace=${workspaceId}&opt_fields=gid,name&limit=100`
        );
      collected.push(...getAsanaProjects(page));
    }
    return collected;
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "asana"),

  getParameters: (choice, filterId): Record<string, string> =>
    choice === "optionB" && filterId
      ? { project_id: filterId }
      : { assignee: "me" },

  getChoiceFromParameters: (parameters) => {
    const projectId =
      getStringCellParam(parameters, "project_id") ??
      getStringCellParam(parameters, "projectId");
    return projectId
      ? { choice: "optionB", filterId: projectId }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Asana", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const pid =
      getStringCellParam(parameters, "project_id") ??
      getStringCellParam(parameters, "projectId");
    return pid ? `${filterItemName(filterItems, pid)} tasks` : "My Tasks";
  },

  getHint: (parameters, filterItems, storedName) => {
    const pid =
      getStringCellParam(parameters, "project_id") ??
      getStringCellParam(parameters, "projectId");
    if (!pid) {
      return "Tasks assigned to you in Asana (My Tasks) will automatically appear in your Routine inbox.";
    }
    const liveName = filterItems.find((p) => p.id === pid)?.name;
    return `Tasks in project "${liveName ?? storedName ?? pid}" will automatically appear in your Routine inbox.`;
  },

  fields: {
    url: "permalink_url",
    scheduled: "due_on",
    completed: "completed",
  },

  completionActions: [
    {
      label: "Complete the task in Asana",
      fields: {
        url: "permalink_url",
        scheduled: "due_on",
        completed: "completed",
      },
    },
    {
      label: "Delete the task",
      fields: {
        url: "permalink_url",
        scheduled: "due_on",
        completed: "is_deleted",
      },
    },
  ],

  illustrationRows: [
    { name: "Finalize Q2 roadmap", project: "Product", completed: true },
    { name: "Review design specs", project: "Design", highlight: true },
    { name: "Write release notes", project: "Engineering" },
    { name: "Sync with growth team", project: "Marketing" },
    { name: "Update onboarding docs", project: "Product" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── ClickUp ──────────────────────────────────────────────────────────────────

export const clickupConfig: IntegrationSyncConfig = {
  integrationKey: "clickup",
  label: "ClickUp",
  description: "Connect your ClickUp account to get your tasks in Routine.",
  collectionKey: "tasks",

  requiresFilter: true,
  configureQuestion: "Which List should sync to Routine?",
  filterOptionA: { icon: "✅", label: "All tasks" },
  filterOptionB: { icon: "📋", label: "Specific List" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const connection = membrane.connection(connId);
    return fetchClickUpListItems((path) => connection.proxy.get(path));
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "clickup"),

  getParameters: (_choice, filterId): Record<string, string> => ({
    listId: filterId,
  }),

  getChoiceFromParameters: (parameters) => {
    const listId = getStringCellParam(parameters, "listId");
    return listId
      ? { choice: "optionB", filterId: listId }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName(
      filterName ? `ClickUp — ${filterName}` : "ClickUp",
      connectionLabel
    ),

  getSyncName: (parameters, filterItems) => {
    const listId = getStringCellParam(parameters, "listId");
    return listId
      ? `${filterItemName(filterItems, listId)} tasks`
      : "All tasks";
  },

  getHint: (parameters, filterItems, storedName) => {
    const listId = getStringCellParam(parameters, "listId");
    const liveName = filterItems.find((list) => list.id === listId)?.name;
    return `Tasks in "${liveName ?? storedName ?? listId ?? "this ClickUp List"}" will automatically appear in your Routine inbox.`;
  },

  fields: {
    url: "url",
    scheduled: "due_date",
    completed: "completed",
  },

  completionActions: [
    {
      label: "Complete the task in ClickUp",
      fields: {
        url: "url",
        scheduled: "due_date",
        completed: "completed",
      },
    },
    {
      label: "Delete the task",
      fields: {
        url: "url",
        scheduled: "due_date",
        completed: "is_deleted",
      },
    },
  ],

  illustrationRows: [
    { name: "Prepare sprint review", project: "Product", completed: true },
    { name: "Review campaign brief", project: "Marketing", highlight: true },
    { name: "Update onboarding flow", project: "Product" },
    { name: "Plan customer interviews", project: "Research" },
    { name: "Write launch checklist", project: "Operations" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── Slack ───────────────────────────────────────────────────────────────────

export const slackConfig: IntegrationSyncConfig = {
  integrationKey: "slack",
  label: "Slack",
  description:
    "Connect your Slack workspace to get saved messages as tasks in Routine.",
  collectionKey: "messages",

  requiresFilter: true,
  configureQuestion: "Which channel should sync saved messages to Routine?",
  filterOptionA: { icon: "🔖", label: "Saved messages" },
  filterOptionB: { icon: "#️⃣", label: "Channel" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("channels")
      .list();
    return getDataCollectionItems(response, ["name"]);
  },

  getParameters: (_choice, filterId): Record<string, string> => ({
    conversationId: filterId,
    filterType: "saved",
  }),

  getChoiceFromParameters: (parameters) => ({
    choice: "optionB",
    filterId: getStringCellParam(parameters, "conversationId") ?? "",
  }),

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Slack", connectionLabel, `#${filterName}`),

  getSyncName: (parameters, filterItems) => {
    const channelId = getStringCellParam(parameters, "conversationId");
    return `#${channelId ? filterItemName(filterItems, channelId) : "channel"} saved messages`;
  },

  getHint: (parameters, filterItems, storedName) => {
    const channelId = getStringCellParam(parameters, "conversationId");
    const liveName = filterItems.find(
      (channel) => channel.id === channelId
    )?.name;
    return `Messages saved for later in #${liveName ?? storedName ?? channelId ?? "this channel"} will automatically appear in your Routine inbox.`;
  },

  fields: {
    url: "Url",
  },

  illustrationRows: [
    { name: "Reply to launch feedback", project: "#product", highlight: true },
    { name: "Review the incident summary", project: "#engineering" },
    { name: "Follow up with the customer", project: "#sales" },
    { name: "Share the hiring plan", project: "#leadership" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── Trello ──────────────────────────────────────────────────────────────────

export const trelloConfig: IntegrationSyncConfig = {
  integrationKey: "trello",
  label: "Trello",
  description: "Connect your Trello account to get your cards as tasks.",
  collectionKey: "cards",

  requiresFilter: true,
  configureQuestion: "Which board should sync to Routine?",
  filterOptionA: { icon: "📋", label: "All cards" },
  filterOptionB: { icon: "📋", label: "Specific board" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("boards")
      .list();
    return getDataCollectionItems(response, ["name"]);
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "trello"),

  getParameters: (_choice, filterId): Record<string, string> => ({
    boardId: filterId,
  }),

  getChoiceFromParameters: (parameters) => {
    const boardId = getStringCellParam(parameters, "boardId");
    return boardId
      ? { choice: "optionB", filterId: boardId }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Trello", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const boardId = getStringCellParam(parameters, "boardId");
    return boardId
      ? `${filterItemName(filterItems, boardId)} cards`
      : "Trello cards";
  },

  getHint: (parameters, filterItems, storedName) => {
    const boardId = getStringCellParam(parameters, "boardId");
    const liveName = filterItems.find((board) => board.id === boardId)?.name;
    return `Cards on board "${liveName ?? storedName ?? boardId ?? "this Trello board"}" will automatically appear in your Routine inbox.`;
  },

  fields: {
    url: "url",
    scheduled: "due",
    completed: "dueComplete",
  },

  completionActions: [
    {
      label: "Mark the card complete in Trello",
      fields: {
        url: "url",
        scheduled: "due",
        completed: "dueComplete",
      },
    },
    {
      label: "Archive the card in Trello",
      fields: {
        url: "url",
        scheduled: "due",
        completed: "closed",
      },
    },
  ],

  illustrationRows: [
    { name: "Prepare sprint review", project: "Product", completed: true },
    { name: "Review customer feedback", project: "Product", highlight: true },
    { name: "Update launch checklist", project: "Marketing" },
    { name: "Plan user interviews", project: "Research" },
    { name: "Document release steps", project: "Operations" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── Attio ────────────────────────────────────────────────────────────────────

export const attioConfig: IntegrationSyncConfig = {
  integrationKey: "attio",
  label: "Attio",
  description: "Connect your Attio account to get your tasks in Routine.",
  collectionKey: "tasks",

  // Attio lists all workspace tasks and has no parametersSchema for filtering
  // them by assignee.
  filterOptionA: { icon: "✅", label: "All tasks" },
  configureQuestion: "Sync Attio tasks into Routine?",

  fetchFilterItems: async () => [],

  fetchConnectionLabel: (connId, membrane) =>
    fetchLabelForConnection(membrane, connId, "attio"),

  getParameters: () => ({}),

  getChoiceFromParameters: () => ({ choice: "optionA", filterId: "" }),

  getDisplayName: (_choice, _filterName, connectionLabel) =>
    getGenericDisplayName("Attio", connectionLabel, "All tasks"),

  getSyncName: () => "All tasks",

  getHint: () =>
    "All Attio workspace tasks will automatically appear in your Routine inbox.",

  // Attio list fields; Membrane exposes the API names as columns.
  // No stable public task URL in the schema, so url_mapping is omitted.
  fields: {
    scheduled: "deadline_at",
    completed: "is_completed",
  },

  completionActions: [
    {
      label: "Complete the task in Attio",
      fields: {
        scheduled: "deadline_at",
        completed: "is_completed",
      },
    },
  ],

  illustrationRows: [
    { name: "Follow up with Acme", project: "Deals", completed: true },
    { name: "Send proposal draft", project: "Deals", highlight: true },
    { name: "Schedule intro call", project: "People" },
    { name: "Update company notes", project: "Companies" },
    { name: "Prep QBR agenda", project: "Deals" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── GitHub ──────────────────────────────────────────────────────────────────

export const githubIssueStatusCells: StatusCellMapping = {
  done: {
    read: [stringCell("closed")],
    write: stringCell("closed"),
  },
  todo: {
    read: [stringCell("open")],
    write: stringCell("open"),
  },
  // GitHub has no discarded issue state. Discarding a Routine task closes the
  // issue, matching the behavior of other two-state issue trackers.
  discarded: {
    read: [],
    write: stringCell("closed"),
  },
};

export const githubConfig: IntegrationSyncConfig = {
  integrationKey: "github",
  label: "GitHub",
  description:
    "Connect your GitHub account to get your issues as tasks in Routine.",
  collectionKey: "issues",

  configureQuestion: "Which issues should become tasks?",
  filterOptionA: { icon: "👤", label: "Issues assigned to me" },
  filterOptionB: { icon: "📁", label: "Specific repository" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("authenticated-user-repositories")
      .list();
    return getGitHubRepositoryItems(response);
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "github"),

  getParameters: getGitHubIssueParameters,

  getChoiceFromParameters: (parameters) => {
    const owner = getStringCellParam(parameters, "owner");
    const repo = getStringCellParam(parameters, "repo");
    return owner && repo
      ? { choice: "optionB", filterId: `${owner}/${repo}` }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("GitHub", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const owner = getStringCellParam(parameters, "owner");
    const repo = getStringCellParam(parameters, "repo");
    const fullName = owner && repo ? `${owner}/${repo}` : undefined;
    return fullName
      ? `${filterItemName(filterItems, fullName)} issues`
      : "Issues assigned to me";
  },

  getHint: (parameters, filterItems, storedName) => {
    const owner = getStringCellParam(parameters, "owner");
    const repo = getStringCellParam(parameters, "repo");
    const fullName = owner && repo ? `${owner}/${repo}` : undefined;
    if (!fullName) {
      return "Issues assigned to you on GitHub will automatically appear in your Routine inbox.";
    }
    const liveName = filterItems.find((item) => item.id === fullName)?.name;
    return `Issues in repository "${liveName ?? storedName ?? fullName}" will automatically appear in your Routine inbox.`;
  },

  fields: {
    url: "html_url",
    completed: "state",
    customStatusCells: githubIssueStatusCells,
  },

  completionActions: [
    {
      label: "Close the issue on GitHub",
      fields: {
        url: "html_url",
        completed: "state",
        customStatusCells: githubIssueStatusCells,
      },
    },
  ],

  illustrationRows: [
    { name: "Fix flaky workflow job", project: "api", completed: true },
    { name: "Handle expired OAuth tokens", project: "api", highlight: true },
    { name: "Improve issue templates", project: "front" },
    { name: "Document the release process", project: "docs" },
    { name: "Remove the legacy endpoint", project: "api" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── GitLab ───────────────────────────────────────────────────────────────────

export const gitlabConfig: IntegrationSyncConfig = {
  integrationKey: "gitlab",
  label: "GitLab",
  description:
    "Connect your GitLab account to get your issues as tasks in Routine.",
  collectionKey: "issues",

  configureQuestion: "Which issues should become tasks?",
  filterOptionA: { icon: "👤", label: "Issues assigned to me" },
  filterOptionB: { icon: "📁", label: "Specific project" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("projects")
      .list();
    // The connector maps `name_with_namespace` ("Group / Project") onto `name`,
    // so two same-named projects stay distinguishable in the picker.
    return getDataCollectionItems(response, ["name", "path"]);
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "gitlab"),

  getParameters: (choice, filterId): Record<string, string> =>
    choice === "optionB" && filterId ? { projectId: filterId } : {},

  getChoiceFromParameters: (parameters) => {
    const projectId = getStringCellParam(parameters, "projectId");
    return projectId
      ? { choice: "optionB", filterId: projectId }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("GitLab", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const projectId = getStringCellParam(parameters, "projectId");
    return projectId
      ? `${filterItemName(filterItems, projectId)} issues`
      : "Issues assigned to me";
  },

  getHint: (parameters, filterItems, storedName) => {
    const projectId = getStringCellParam(parameters, "projectId");
    if (!projectId) {
      return "Issues assigned to you in GitLab will automatically appear in your Routine inbox.";
    }
    const liveName = filterItems.find((p) => p.id === projectId)?.name;
    return `Issues in project "${liveName ?? storedName ?? projectId}" will automatically appear in your Routine inbox.`;
  },

  // GitLab issues have exactly two states, which the connector exposes as a
  // writable `completed` boolean (mapped to `state_event: close | reopen`), so
  // the default boolean status cells are correct — no per-workspace status ids
  // to resolve at bind time the way Notion and Featurebase need.
  fields: {
    url: "web_url",
    scheduled: "due_date",
    completed: "completed",
  },

  // Only one action: `DELETE /projects/:id/issues/:iid` needs the Owner role
  // and destroys the issue for everyone, so there is no "Delete the task"
  // counterpart to the one Todoist and Asana offer.
  completionActions: [
    {
      label: "Close the issue in GitLab",
      fields: {
        url: "web_url",
        scheduled: "due_date",
        completed: "completed",
      },
    },
  ],

  illustrationRows: [
    { name: "Fix flaky pipeline job", project: "infra", completed: true },
    { name: "Bump runner image to 24.04", project: "infra", highlight: true },
    { name: "Add retry to webhook worker", project: "backend" },
    { name: "Document the release process", project: "docs" },
    { name: "Drop the legacy auth endpoint", project: "backend" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── Jira ─────────────────────────────────────────────────────────────────────

/**
 * Jira status ids the connector can be asked to transition to, as a mapping
 * step property.
 *
 * Jira has exactly one completion-bearing field — the issue's status — so
 * unlike Notion there is nothing to choose between; the single property exists
 * so the shared mapping step can render its values. Statuses come from
 * `GET /rest/api/3/project/{key}/statuses`, which groups them by issue type,
 * so they are flattened and de-duplicated by id.
 *
 * The `group` carries Jira's status *category* ("To Do" / "In Progress" /
 * "Done"), which is what drives the defaults below and what the UI shows
 * beside each value.
 */
export function jiraTaskMappingProperties(
  response: unknown
): TaskSyncMappingProperty[] {
  const seen = new Set<string>();
  const options: Array<{ id: string; name: string; group?: string }> = [];

  for (const issueType of Array.isArray(response) ? response : []) {
    for (const status of arrayProperty(issueType, "statuses") ?? []) {
      const id = optionalStringProperty(status, "id");
      if (!id || seen.has(id)) continue;
      seen.add(id);
      // `optionalStringProperty` can yield null; the option's group is
      // `string | undefined`, so normalise before spreading it in.
      const category = property(status, "statusCategory");
      const group = optionalStringProperty(category, "name") ?? undefined;
      options.push({
        id,
        name: optionalStringProperty(status, "name") ?? id,
        ...(group ? { group } : {}),
      });
    }
  }

  if (options.length === 0) return [];
  return [
    { id: "status", name: "Status", kind: "status", options },
    // Jira's due date is a fixed field rather than a choice, but the step
    // renders a date picker, so offer it as the single candidate.
    { id: "duedate", name: "Due date", kind: "date", options: [] },
  ];
}

/** Status names that conventionally mean "closed without doing it". */
const JIRA_DISCARDED_NAME =
  /won'?t (do|fix)|cancel|duplicate|declin|reject|abandon/i;

/**
 * Sensible defaults for a Jira project's statuses.
 *
 * Everything in the Done category counts as complete, except statuses whose
 * name says the work was dropped rather than finished — those default to
 * Discarded, which is the distinction a boolean `completed` column cannot
 * express and the whole reason this mapping exists. Everything else is Todo.
 */
export function defaultJiraTaskMapping(
  properties: TaskSyncMappingProperty[]
): TaskSyncMapping {
  const statusProperty = properties.find(
    (candidate) => candidate.id === "status"
  );
  const dateProperty = properties.find(
    (candidate) => candidate.kind === "date"
  );
  if (!statusProperty) {
    return { statusPropertyId: "", datePropertyId: dateProperty?.id ?? "" };
  }

  const isDoneCategory = (group: string | undefined) =>
    (group ?? "").toLowerCase() === "done";

  const closed = statusProperty.options.filter((option) =>
    isDoneCategory(option.group)
  );
  const discarded = closed.filter((option) =>
    JIRA_DISCARDED_NAME.test(option.name)
  );
  const done = closed.filter(
    (option) => !JIRA_DISCARDED_NAME.test(option.name)
  );
  const todo = statusProperty.options.filter(
    (option) => !isDoneCategory(option.group)
  );

  // Prefer the canonical status of each bucket so completing a task does not
  // resolve an issue as something unexpected.
  const doneWrite =
    done.find((option) => option.name.toLowerCase() === "done")?.id ??
    done[0]?.id ??
    closed[0]?.id;
  const todoWrite =
    todo.find((option) => (option.group ?? "").toLowerCase() === "to do")?.id ??
    todo[0]?.id;
  if (!doneWrite || !todoWrite) {
    return { statusPropertyId: "", datePropertyId: dateProperty?.id ?? "" };
  }

  return {
    statusPropertyId: statusProperty.id,
    datePropertyId: dateProperty?.id ?? "",
    statusCells: {
      done: {
        read: done.map((o) => stringCell(o.id)),
        write: stringCell(doneWrite),
      },
      todo: {
        read: todo.map((o) => stringCell(o.id)),
        write: stringCell(todoWrite),
      },
      discarded: {
        read: discarded.map((o) => stringCell(o.id)),
        // With no dedicated "won't do" status, discarding still has to close
        // the issue — leaving it open would desync the task from Jira.
        write: stringCell(discarded[0]?.id ?? doneWrite),
      },
    },
  };
}

/**
 * Resolves the Jira status cells for a sync at bind time.
 *
 * Only project-scoped syncs can have one: status ids belong to a project's
 * workflow, and the "assigned to me" and saved-filter scopes span projects
 * with different workflows and different ids. Those keep the boolean
 * `completed` column instead — see `jiraConfig.fieldsFor`.
 */
async function resolveJiraStatusCells(
  connId: string,
  parameters: Record<string, unknown>,
  membrane: MembraneClient
): Promise<StatusCellResolution> {
  const stored = storedStatusCells(parameters);
  if (stored) return { kind: "resolved", cells: stored };

  const projectId = getStringCellParam(parameters, "projectId");
  if (!projectId) return { kind: "unavailable" };

  const response: unknown = await membrane
    .connection(connId)
    .proxy.get(`/rest/api/3/project/${encodeURIComponent(projectId)}/statuses`);
  const cells = defaultJiraTaskMapping(
    jiraTaskMappingProperties(response)
  ).statusCells;
  return cells ? { kind: "resolved", cells } : { kind: "unavailable" };
}

/** Completion bound to the issue's status id, for project-scoped syncs. */
const jiraStatusFields: FieldNames = {
  url: "url",
  scheduled: "duedate",
  completed: "statusId",
  resolveStatusCells: resolveJiraStatusCells,
  // `statusId` holds Jira status ids, so the boolean fallback would write
  // `true`/`false` into it and every completion would fail against Jira while
  // the binding looked configured.
  requireResolvedStatusCells: true,
};

/** Completion bound to the derived boolean, for the cross-project scopes. */
const jiraBooleanFields: FieldNames = {
  url: "url",
  scheduled: "duedate",
  completed: "completed",
};

/** Whether a sync is scoped to a single project, and so can map statuses. */
const jiraIsProjectScoped = (parameters: Record<string, unknown>) =>
  !!getStringCellParam(parameters, "projectId") &&
  !getStringCellParam(parameters, "filterId");

export const jiraConfig: IntegrationSyncConfig = {
  integrationKey: "jira",
  label: "Jira",
  description:
    "Connect your Jira account to get your issues as tasks in Routine.",
  collectionKey: "issues",

  configureQuestion: "Which issues should become tasks?",
  filterOptionA: { icon: "👤", label: "Issues assigned to me" },
  filterOptionB: { icon: "📁", label: "Specific project" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("projects")
      .list();
    return getDataCollectionItems(response, ["name", "key"]);
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "jira"),

  // Only a project id is sent. An empty payload is "assigned to me". A saved
  // filter used to be a third scope; new syncs no longer create one.
  getParameters: (choice, filterId, mapping): Record<string, string> => {
    if (choice === "optionB" && filterId) {
      return {
        projectId: filterId,
        // Only a project-scoped sync carries a status mapping; "assigned to me"
        // skips the mapping step (see taskMapping.appliesTo).
        ...(mapping ? statusMappingParameters(mapping) : {}),
      };
    }
    return {};
  },

  getChoiceFromParameters: (parameters) => {
    const savedFilterId = getStringCellParam(parameters, "filterId");
    if (savedFilterId) return { choice: "optionC", filterId: savedFilterId };
    const projectId = getStringCellParam(parameters, "projectId");
    return projectId
      ? { choice: "optionB", filterId: projectId }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Jira", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const savedFilterId = getStringCellParam(parameters, "filterId");
    if (savedFilterId) {
      return `${filterItemName(filterItems, savedFilterId)} issues`;
    }
    const projectId = getStringCellParam(parameters, "projectId");
    return projectId
      ? `${filterItemName(filterItems, projectId)} issues`
      : "Issues assigned to me";
  },

  getHint: (parameters, filterItems, storedName) => {
    const savedFilterId = getStringCellParam(parameters, "filterId");
    if (savedFilterId) {
      const liveName = filterItems.find((f) => f.id === savedFilterId)?.name;
      return `Issues matching the "${liveName ?? storedName ?? savedFilterId}" filter will automatically appear in your Routine inbox.`;
    }
    const projectId = getStringCellParam(parameters, "projectId");
    if (!projectId) {
      return "Issues assigned to you in Jira will automatically appear in your Routine inbox.";
    }
    const liveName = filterItems.find((p) => p.id === projectId)?.name;
    return `Issues in project "${liveName ?? storedName ?? projectId}" will automatically appear in your Routine inbox.`;
  },

  // Jira has no writable status field: the connector turns both `completed`
  // and an explicit `statusId` into a workflow transition.
  //
  // Which column completion binds to depends on the scope. A project-scoped
  // sync binds `statusId`, so Done and Discarded can write different Jira
  // statuses ("Done" vs "Won't Do") — impossible with a boolean, where
  // discarding writes the same `true` as completing. The cross-project scopes
  // keep the boolean, because status ids differ per project workflow.
  fields: jiraBooleanFields,
  fieldsFor: (parameters) =>
    jiraIsProjectScoped(parameters) ? jiraStatusFields : jiraBooleanFields,

  taskMapping: {
    appliesTo: (choice, filterId) => choice === "optionB" && !!filterId,
    fetchProperties: async (connId, projectId, membrane) => {
      const response: unknown = await membrane
        .connection(connId)
        .proxy.get(
          `/rest/api/3/project/${encodeURIComponent(projectId)}/statuses`
        );
      return jiraTaskMappingProperties(response);
    },
    defaultMapping: defaultJiraTaskMapping,
  },

  // Deleting a Jira issue needs the Delete Issues permission and removes it
  // for the whole project, so there is no "Delete the task" counterpart to the
  // one Todoist and Asana offer. The two entries are the same action against
  // different columns, and `visibleFor` makes each sync see exactly one.
  completionActions: [
    {
      label: "Transition the issue in Jira",
      fields: jiraStatusFields,
      visibleFor: jiraIsProjectScoped,
    },
    {
      label: "Complete the issue in Jira",
      fields: jiraBooleanFields,
      visibleFor: (parameters) => !jiraIsProjectScoped(parameters),
    },
  ],

  illustrationRows: [
    { name: "Fix OAuth token refresh", project: "ENG", completed: true },
    { name: "Add rate limiting to the API", project: "ENG", highlight: true },
    { name: "Migrate the billing schema", project: "PLAT" },
    { name: "Write the incident postmortem", project: "OPS" },
    { name: "Triage inbound bug reports", project: "SUP" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── Google Tasks ────────────────────────────────────────────────────────────

export const googleTasksStatusCells: StatusCellMapping = {
  done: {
    read: [stringCell("completed")],
    write: stringCell("completed"),
  },
  todo: {
    read: [stringCell("needsAction")],
    write: stringCell("needsAction"),
  },
  // Google Tasks has no discarded state. Discarding in Routine completes the
  // Google task, matching the boolean completion integrations.
  discarded: {
    read: [],
    write: stringCell("completed"),
  },
};

export const googleTasksConfig: IntegrationSyncConfig = {
  integrationKey: "google-tasks",
  label: "Google Tasks",
  description:
    "Connect your Google Tasks account to get your tasks in Routine.",
  collectionKey: "tasks",

  // Google Tasks has no all-tasks endpoint: every request is scoped to one
  // task list, and users can create another sync for each additional list.
  requiresFilter: true,
  configureQuestion: "Which list should sync to Routine?",
  filterOptionA: { icon: "✅", label: "All tasks" },
  filterOptionB: { icon: "📋", label: "Specific list" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("task-lists")
      .list();
    return getDataCollectionItems(response, ["title", "name"]);
  },

  getParameters: (_choice, filterId): Record<string, string> => ({
    taskListId: filterId,
  }),

  getChoiceFromParameters: (parameters) => {
    const taskListId = getStringCellParam(parameters, "taskListId");
    return taskListId
      ? { choice: "optionB", filterId: taskListId }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Google Tasks", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const taskListId = getStringCellParam(parameters, "taskListId");
    return taskListId
      ? `${filterItemName(filterItems, taskListId)} tasks`
      : "Google tasks";
  },

  getHint: (parameters, filterItems, storedName) => {
    const taskListId = getStringCellParam(parameters, "taskListId");
    const liveName = filterItems.find((list) => list.id === taskListId)?.name;
    return `Tasks in list "${liveName ?? storedName ?? taskListId}" will automatically appear in your Routine inbox.`;
  },

  fields: {
    url: "webViewLink",
    scheduled: "due",
    completed: "status",
    customStatusCells: googleTasksStatusCells,
  },

  completionActions: [
    {
      label: "Complete the task in Google Tasks",
      fields: {
        url: "webViewLink",
        scheduled: "due",
        completed: "status",
        customStatusCells: googleTasksStatusCells,
      },
    },
  ],

  illustrationRows: [
    { name: "Reply to project update", project: "Work", completed: true },
    { name: "Prepare weekly priorities", project: "Work", highlight: true },
    { name: "Book dentist appointment", project: "Personal" },
    { name: "Pick up groceries", project: "Personal" },
    { name: "Plan weekend trip", project: "Travel" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── Microsoft To Do ──────────────────────────────────────────────────────────

export const microsoftToDoConfig: IntegrationSyncConfig = {
  integrationKey: "microsoft-to-do",
  label: "MS To Do",
  description:
    "Connect your Microsoft To Do account to get your tasks in Routine.",
  collectionKey: "tasks",

  // Microsoft To Do has no "all tasks" endpoint — tasks are always scoped to a
  // list — so a task list must be selected before the collection can be enabled.
  requiresFilter: true,
  configureQuestion: "Which list should sync to Routine?",

  // filterOptionA is unused when requiresFilter is set (no "all" card is shown);
  // filterOptionB drives the single required list picker.
  filterOptionA: { icon: "✅", label: "All tasks" },
  filterOptionB: { icon: "📋", label: "Specific list" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("task-lists")
      .list();
    return getDataCollectionItems(response, ["displayName", "name"]);
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "microsoft-to-do"),

  // A list is always required (see requiresFilter), so the parameters always
  // carry the selected taskListId — never an empty map.
  getParameters: (_choice, filterId): Record<string, string> => ({
    taskListId: filterId,
  }),

  getChoiceFromParameters: (parameters) => {
    const taskListId = getStringCellParam(parameters, "taskListId");
    return taskListId
      ? { choice: "optionB", filterId: taskListId }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Microsoft To Do", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const lid = getStringCellParam(parameters, "taskListId");
    return lid ? `${filterItemName(filterItems, lid)} tasks` : "All tasks";
  },

  getHint: (parameters, filterItems, storedName) => {
    const lid = getStringCellParam(parameters, "taskListId");
    if (!lid) {
      return "All your Microsoft To Do tasks will automatically appear in your Routine inbox.";
    }
    const liveName = filterItems.find((l) => l.id === lid)?.name;
    return `Tasks in list "${liveName ?? storedName ?? lid}" will automatically appear in your Routine inbox.`;
  },

  // The connector now exposes `dueDateTime` as a nullable date, so it can back
  // the scheduled column. This mapping was previously omitted because the
  // column used to be a {dateTime, timeZone} record: binding it wrote an empty
  // record and MS Graph rejected the update with a 400 ("Failed to update
  // record"). If planning from Routine starts failing that way again, check
  // that the connector still converts the date back into MS Graph's
  // {dateTime, timeZone} shape on update.
  fields: {
    scheduled: "dueDateTime",
    completed: "status",
    customStatusCells: {
      done: {
        read: [{ type: "string", value: "completed" }],
        write: { type: "string", value: "completed" },
      },
      todo: {
        read: [
          { type: "string", value: "notStarted" },
          { type: "string", value: "inProgress" },
          { type: "string", value: "waitingOnOthers" },
        ],
        write: { type: "string", value: "notStarted" },
      },
      discarded: {
        read: [{ type: "string", value: "deferred" }],
        write: { type: "string", value: "deferred" },
      },
    },
  },

  completionActions: [
    {
      label: "Complete the task in MS To Do",
      fields: {
        completed: "status",
        customStatusCells: {
          done: {
            read: [{ type: "string", value: "completed" }],
            write: { type: "string", value: "completed" },
          },
          todo: {
            read: [
              { type: "string", value: "notStarted" },
              { type: "string", value: "inProgress" },
              { type: "string", value: "waitingOnOthers" },
            ],
            write: { type: "string", value: "notStarted" },
          },
          discarded: {
            read: [{ type: "string", value: "deferred" }],
            write: { type: "string", value: "deferred" },
          },
        },
      },
    },
    {
      label: "Defer the task",
      fields: {
        completed: "status",
        customStatusCells: {
          done: {
            read: [{ type: "string", value: "deferred" }],
            write: { type: "string", value: "deferred" },
          },
          todo: {
            read: [
              { type: "string", value: "notStarted" },
              { type: "string", value: "inProgress" },
              { type: "string", value: "waitingOnOthers" },
            ],
            write: { type: "string", value: "notStarted" },
          },
          discarded: {
            read: [{ type: "string", value: "completed" }],
            write: { type: "string", value: "completed" },
          },
        },
      },
    },
  ],

  illustrationRows: [
    { name: "Prepare quarterly report", project: "Work", completed: true },
    { name: "Review team pull requests", project: "Work", highlight: true },
    { name: "Update project timeline", project: "Work" },
    { name: "Schedule performance reviews", project: "Personal" },
    { name: "Plan weekend trip", project: "Personal" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

export const microsoftPlannerConfig: IntegrationSyncConfig = {
  integrationKey: "microsoft-planner",
  label: "Microsoft Planner",
  description: "Connect Microsoft Planner to get tasks from a plan in Routine.",
  collectionKey: "tasks",

  requiresFilter: true,
  configureQuestion: "Which plan should sync tasks to Routine?",
  filterOptionA: { icon: "📋", label: "Plan tasks" },
  filterOptionB: { icon: "📋", label: "Plan" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("plans")
      .list();
    return getDataCollectionItems(response, ["title", "displayName", "name"]);
  },

  fetchConnectionLabel: (connId, membrane) =>
    fetchLabelForConnection(membrane, connId, "microsoft-planner"),

  getParameters: (_choice, filterId): Record<string, string> => ({
    planId: filterId,
  }),

  getChoiceFromParameters: (parameters) => {
    const planId = getStringCellParam(parameters, "planId");
    return {
      choice: "optionB",
      filterId: planId ?? "",
    };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Microsoft Planner", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const planId = getStringCellParam(parameters, "planId");
    return planId
      ? `${filterItemName(filterItems, planId)} tasks`
      : "Plan tasks";
  },

  getHint: (parameters, filterItems, storedName) => {
    const planId = getStringCellParam(parameters, "planId");
    const liveName = planId
      ? filterItems.find((item) => item.id === planId)?.name
      : undefined;
    return `Tasks in ${liveName ?? storedName ?? "this plan"} will automatically appear in your Routine inbox. Checking a task in Routine completes it in Planner.`;
  },

  fields: {
    url: "webUrl",
    scheduled: "dueDateTime",
    completed: "status",
    customStatusCells: {
      done: {
        read: [{ type: "string", value: "completed" }],
        write: { type: "string", value: "completed" },
      },
      todo: {
        read: [
          { type: "string", value: "notStarted" },
          { type: "string", value: "inProgress" },
        ],
        write: { type: "string", value: "notStarted" },
      },
      discarded: {
        read: [],
        write: { type: "string", value: "completed" },
      },
    },
  },

  completionActions: [
    {
      label: "Complete the task in Planner",
      fields: {
        completed: "status",
        customStatusCells: {
          done: {
            read: [{ type: "string", value: "completed" }],
            write: { type: "string", value: "completed" },
          },
          todo: {
            read: [
              { type: "string", value: "notStarted" },
              { type: "string", value: "inProgress" },
            ],
            write: { type: "string", value: "notStarted" },
          },
          discarded: {
            read: [],
            write: { type: "string", value: "completed" },
          },
        },
      },
    },
  ],

  illustrationRows: [
    { name: "Draft the launch checklist", project: "Product", highlight: true },
    { name: "Review the sprint board", project: "Engineering" },
    { name: "Confirm vendor dates", project: "Operations", completed: true },
    { name: "Share the status update", project: "Product" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

/** Composite picker id for a Teams channel, whose collection needs both IDs. */
export const teamChannelFilterId = (
  teamId: string,
  channelId: string
): string => `${teamId}/${channelId}`;

export const parseTeamChannelFilterId = (
  filterId: string
): { team_id: string; channel_id: string } | undefined => {
  const sep = filterId.indexOf("/");
  if (sep <= 0 || sep === filterId.length - 1) return undefined;
  return {
    team_id: filterId.slice(0, sep),
    channel_id: filterId.slice(sep + 1),
  };
};

export const microsoftTeamsConfig: IntegrationSyncConfig = {
  integrationKey: "microsoft-teams",
  label: "Microsoft Teams",
  description:
    "Connect Microsoft Teams to get channel messages as tasks in Routine.",
  collectionKey: "channel-messages",

  requiresFilter: true,
  configureQuestion: "Which channel should sync messages to Routine?",
  filterOptionA: { icon: "💬", label: "Channel messages" },
  filterOptionB: { icon: "#", label: "Channel" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const connection = membrane.connection(connId);
    const teamsResponse: unknown = await connection
      .dataCollection("teams")
      .list();
    const teams = getDataCollectionItems(teamsResponse, [
      "displayName",
      "name",
    ]);
    const items: FilterItem[] = [];
    for (const team of teams) {
      const channelsResponse: unknown = await connection
        .dataCollection("channels", { team_id: team.id })
        .list();
      for (const channel of getDataCollectionItems(channelsResponse, [
        "displayName",
        "name",
      ])) {
        items.push({
          id: teamChannelFilterId(team.id, channel.id),
          name: `${team.name} / ${channel.name}`,
        });
      }
    }
    return items;
  },

  fetchConnectionLabel: (connId, membrane) =>
    fetchLabelForConnection(membrane, connId, "microsoft-teams"),

  getParameters: (_choice, filterId): Record<string, string> => {
    const parsed = parseTeamChannelFilterId(filterId);
    return parsed ?? {};
  },

  getChoiceFromParameters: (parameters) => {
    const teamId = getStringCellParam(parameters, "team_id");
    const channelId = getStringCellParam(parameters, "channel_id");
    return {
      choice: "optionB",
      filterId:
        teamId && channelId ? teamChannelFilterId(teamId, channelId) : "",
    };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Microsoft Teams", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const teamId = getStringCellParam(parameters, "team_id");
    const channelId = getStringCellParam(parameters, "channel_id");
    const filterId =
      teamId && channelId ? teamChannelFilterId(teamId, channelId) : undefined;
    return filterId
      ? `${filterItemName(filterItems, filterId)} messages`
      : "Channel messages";
  },

  getHint: (parameters, filterItems, storedName) => {
    const teamId = getStringCellParam(parameters, "team_id");
    const channelId = getStringCellParam(parameters, "channel_id");
    const filterId =
      teamId && channelId ? teamChannelFilterId(teamId, channelId) : undefined;
    const liveName = filterId
      ? filterItems.find((item) => item.id === filterId)?.name
      : undefined;
    return `Messages in ${liveName ?? storedName ?? "this channel"} will automatically appear in your Routine inbox. Checking a task in Routine does not change the message in Teams.`;
  },

  // Channel messages have no completion field in Graph; checking in Routine is
  // stored on the binding row, like Slack saved messages.
  fields: {
    url: "webUrl",
  },

  illustrationRows: [
    { name: "Reply to the launch thread", project: "Product", highlight: true },
    { name: "Share the incident summary", project: "Engineering" },
    { name: "Follow up with the customer", project: "Sales" },
    { name: "Post the hiring update", project: "People" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── Linear ───────────────────────────────────────────────────────────────────

export const linearStatusCells: StatusCellMapping = {
  done: {
    read: [stringCell("completed")],
    write: stringCell("completed"),
  },
  todo: {
    read: [
      stringCell("triage"),
      stringCell("backlog"),
      stringCell("unstarted"),
      stringCell("started"),
    ],
    write: stringCell("unstarted"),
  },
  discarded: {
    read: [stringCell("canceled")],
    write: stringCell("canceled"),
  },
};

export const linearTaskMappingProperties: TaskSyncMappingProperty[] = [
  {
    id: "stateType",
    name: "Status",
    kind: "status",
    options: [
      { id: "triage", name: "Triage", group: "To-do" },
      { id: "backlog", name: "Backlog", group: "To-do" },
      { id: "unstarted", name: "Todo", group: "To-do" },
      { id: "started", name: "In progress", group: "To-do" },
      { id: "completed", name: "Done", group: "Complete" },
      { id: "canceled", name: "Canceled", group: "Canceled" },
    ],
  },
];

export function defaultLinearTaskMapping(): TaskSyncMapping {
  return {
    statusPropertyId: "stateType",
    datePropertyId: "",
    statusCells: linearStatusCells,
  };
}

async function resolveLinearStatusCells(
  _connId: string,
  parameters: Record<string, unknown>
): Promise<StatusCellResolution> {
  return {
    kind: "resolved",
    cells: storedStatusCells(parameters) ?? linearStatusCells,
  };
}

export const linearConfig: IntegrationSyncConfig = {
  integrationKey: "linear",
  label: "Linear",
  description: "Connect your Linear account to get your issues in Routine.",
  collectionKey: "issues",

  filterOptionA: { icon: "✅", label: "My issues" },
  filterOptionB: { icon: "👥", label: "Specific team" },
  configureQuestion: "Which Linear issues should sync to Routine?",

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("teams")
      .list();
    return getDataCollectionItems(response, ["name"]);
  },

  fetchConnectionLabel: (connId, membrane) =>
    fetchLabelForConnection(membrane, connId, "linear"),

  getParameters: (choice, filterId, mapping): Record<string, string> => ({
    ...(choice === "optionB" && filterId
      ? { teamId: filterId }
      : { assignee: "me" }),
    ...(mapping ? statusMappingParameters(mapping) : {}),
  }),

  getChoiceFromParameters: (parameters) => {
    const teamId = getStringCellParam(parameters, "teamId");
    return teamId
      ? { choice: "optionB", filterId: teamId }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Linear", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const teamId = getStringCellParam(parameters, "teamId");
    return teamId
      ? `${filterItemName(filterItems, teamId)} issues`
      : "My issues";
  },

  getHint: (parameters, filterItems, storedName) => {
    const teamId = getStringCellParam(parameters, "teamId");
    if (!teamId) {
      return "Issues assigned to you in Linear will automatically appear in your Routine inbox.";
    }
    const liveName = filterItems.find((team) => team.id === teamId)?.name;
    return `Issues on the "${liveName ?? storedName ?? teamId}" team will automatically appear in your Routine inbox.`;
  },

  fields: {
    url: "url",
    scheduled: "dueDate",
    completed: "stateType",
    resolveStatusCells: resolveLinearStatusCells,
    requireResolvedStatusCells: true,
  },

  taskMapping: {
    fetchProperties: async () => linearTaskMappingProperties,
    defaultMapping: defaultLinearTaskMapping,
  },

  completionActions: [
    {
      label: "Update the issue status in Linear",
      fields: {
        url: "url",
        scheduled: "dueDate",
        completed: "stateType",
        resolveStatusCells: resolveLinearStatusCells,
        requireResolvedStatusCells: true,
      },
    },
  ],

  illustrationRows: [
    { name: "Fix login redirect", project: "ENG", completed: true },
    { name: "Ship billing webhook", project: "ENG", highlight: true },
    { name: "Design empty states", project: "Design" },
    { name: "Write launch checklist", project: "Product" },
    { name: "Triage support tickets", project: "Support" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── Notion ──────────────────────────────────────────────────────────────────

/**
 * Notion's default status groups. Group names are stable Notion concepts, while
 * the option labels inside them are user-authored (and localised), so grouping
 * is what the mapping keys off.
 */
const NOTION_COMPLETE_GROUP = "Complete";
const NOTION_TODO_GROUP = "To-do";

/**
 * Flattens a Notion rich-text array (as database titles are) to display text.
 * Accepts a plain string too, since not every field is rich text.
 */
function notionPlainText(value: unknown): string | undefined {
  if (typeof value === "string") return value || undefined;
  if (!Array.isArray(value)) return undefined;
  const text = value
    .map((run) => optionalStringProperty(run, "plain_text") ?? "")
    .join("");
  return text || undefined;
}

/** Option IDs belonging to the named status group, empty if there is none. */
function notionGroupOptionIds(
  status: Record<string, unknown>,
  groupName: string
): Set<string> {
  for (const group of arrayProperty(status, "groups") ?? []) {
    if (optionalStringProperty(group, "name") !== groupName) continue;
    const ids = new Set<string>();
    for (const id of arrayProperty(group, "option_ids") ?? []) {
      if (typeof id === "string") ids.add(id);
    }
    return ids;
  }
  return new Set();
}

/**
 * Builds the status cells from the first `status` property of the database.
 *
 * Cells hold option *IDs*, which survive a user renaming the option in Notion.
 */
function notionStatusPropertyCells(
  properties: Record<string, unknown>
): StatusCellMapping | undefined {
  for (const prop of Object.values(properties)) {
    if (optionalStringProperty(prop, "type") !== "status") continue;
    const status = property(prop, "status");
    if (!isRecord(status)) continue;

    const optionIds: string[] = [];
    for (const option of arrayProperty(status, "options") ?? []) {
      const id = optionalStringProperty(option, "id");
      if (id) optionIds.push(id);
    }

    const completeIds = notionGroupOptionIds(status, NOTION_COMPLETE_GROUP);
    const todoIds = notionGroupOptionIds(status, NOTION_TODO_GROUP);

    // Everything outside "Complete" counts as still open, including custom
    // groups. The native integration did the same (defaulting unknown groups to
    // In progress rather than dropping them): an option that matched neither
    // read set would make its rows show up as neither done nor to-do.
    const done = optionIds.filter((id) => completeIds.has(id));
    const open = optionIds.filter((id) => !completeIds.has(id));

    const doneWrite = done[0];
    // Prefer a genuine "To-do" option for the un-complete write, since the
    // first open option may well be an In progress one.
    const openWrite = open.find((id) => todoIds.has(id)) ?? open[0];
    if (!doneWrite || !openWrite) continue;

    return {
      done: { read: done.map(stringCell), write: stringCell(doneWrite) },
      todo: { read: open.map(stringCell), write: stringCell(openWrite) },
      // Notion has no "discarded" concept, so nothing reads as discarded, but
      // discarding a task still marks the page done — matching the shape of the
      // boolean default mapping.
      discarded: { read: [], write: stringCell(doneWrite) },
    };
  }
  return undefined;
}

/**
 * Fallback for databases that model completion with a checkbox rather than a
 * status property, which is a very common Notion pattern.
 *
 * `fields-from-api.js` promotes a checkbox into `routine_status` as the string
 * "true"/"false", so both database shapes map onto the same Routine column.
 */
function notionCheckboxCells(
  properties: Record<string, unknown>
): StatusCellMapping | undefined {
  const hasCheckbox = Object.values(properties).some(
    (prop) => optionalStringProperty(prop, "type") === "checkbox"
  );
  if (!hasCheckbox) return undefined;

  return {
    done: { read: [stringCell("true")], write: stringCell("true") },
    todo: { read: [stringCell("false")], write: stringCell("false") },
    discarded: { read: [], write: stringCell("true") },
  };
}

function notionPropertyOptions(
  propertyValue: Record<string, unknown>,
  kind: "select" | "status"
): Array<{ id: string; name: string; group?: string }> {
  const details = property(propertyValue, kind);
  if (!isRecord(details)) return [];
  const groupByOption = new Map<string, string>();
  if (kind === "status") {
    for (const group of arrayProperty(details, "groups") ?? []) {
      const groupName = optionalStringProperty(group, "name");
      if (!groupName) continue;
      for (const optionId of arrayProperty(group, "option_ids") ?? []) {
        if (typeof optionId === "string")
          groupByOption.set(optionId, groupName);
      }
    }
  }
  return (arrayProperty(details, "options") ?? []).flatMap((option) => {
    const id = optionalStringProperty(option, "id");
    const name = optionalStringProperty(option, "name");
    if (!id || !name) return [];
    const group = groupByOption.get(id);
    return [{ id, name, ...(group ? { group } : {}) }];
  });
}

export function notionTaskMappingProperties(
  response: unknown,
  databaseId: string
): TaskSyncMappingProperty[] {
  const record = (arrayProperty(response, "records") ?? []).find(
    (candidate) => optionalStringProperty(candidate, "id") === databaseId
  );
  const properties = property(property(record, "fields"), "properties");
  if (!isRecord(properties)) return [];

  const result: TaskSyncMappingProperty[] = [];
  for (const [fallbackName, value] of Object.entries(properties)) {
    if (!isRecord(value)) continue;
    const type = optionalStringProperty(value, "type");
    if (
      type !== "select" &&
      type !== "status" &&
      type !== "checkbox" &&
      type !== "date"
    ) {
      continue;
    }
    const id = optionalStringProperty(value, "id");
    if (!id) continue;
    const name = optionalStringProperty(value, "name") ?? fallbackName;
    result.push({
      id,
      name,
      kind: type,
      options:
        type === "select" || type === "status"
          ? notionPropertyOptions(value, type)
          : [],
    });
  }
  return result.sort((a, b) => a.name.localeCompare(b.name));
}

function mappingForProperty(
  propertyValue: TaskSyncMappingProperty
): StatusCellMapping | undefined {
  if (propertyValue.kind === "checkbox") {
    return {
      done: { read: [stringCell("true")], write: stringCell("true") },
      todo: { read: [stringCell("false")], write: stringCell("false") },
      discarded: { read: [], write: stringCell("true") },
    };
  }
  const done = propertyValue.options.filter(
    (option) => option.group?.toLowerCase() === "complete"
  );
  const todo = propertyValue.options.filter(
    (option) => option.group?.toLowerCase() !== "complete"
  );
  const doneWrite = done[0]?.id;
  const todoWrite =
    todo.find((option) => option.group?.toLowerCase() === "to-do")?.id ??
    todo[0]?.id;
  if (!doneWrite || !todoWrite) return undefined;
  return {
    done: {
      read: done.map((option) => stringCell(option.id)),
      write: stringCell(doneWrite),
    },
    todo: {
      read: todo.map((option) => stringCell(option.id)),
      write: stringCell(todoWrite),
    },
    discarded: { read: [], write: stringCell(doneWrite) },
  };
}

export function defaultNotionTaskMapping(
  properties: TaskSyncMappingProperty[]
): TaskSyncMapping {
  // Status groups provide useful defaults; checkboxes are inherently binary.
  // A select has no completion semantics, but preselect it when it is the only
  // candidate so the wizard can require the user to map its values explicitly.
  const statusProperty =
    properties.find(
      (candidate) =>
        candidate.kind === "status" &&
        mappingForProperty(candidate) !== undefined
    ) ??
    properties.find((candidate) => candidate.kind === "checkbox") ??
    properties.find((candidate) => candidate.kind === "select");
  const statusCells = statusProperty
    ? mappingForProperty(statusProperty)
    : undefined;
  const dateProperty = properties.find(
    (candidate) => candidate.kind === "date"
  );
  return {
    statusPropertyId: statusProperty?.id ?? "",
    datePropertyId: dateProperty?.id ?? "",
    ...(statusCells ? { statusCells } : {}),
  };
}

function cellStringValue(cell: Cell): string | undefined {
  return typeof cell === "object" &&
    cell !== null &&
    "type" in cell &&
    cell.type === "string"
    ? cell.value
    : undefined;
}

function statusMappingParameters(
  mapping: TaskSyncMapping
): Record<string, string> {
  const cells = mapping.statusCells;
  if (!mapping.statusPropertyId || !cells) return {};
  const values = (items: Cell[]) =>
    JSON.stringify(items.flatMap((cell) => cellStringValue(cell) ?? []));
  return {
    status_property_id: mapping.statusPropertyId,
    status_todo_ids: values(cells.todo.read),
    status_done_ids: values(cells.done.read),
    status_discarded_ids: values(cells.discarded.read),
    status_todo_write_id: cellStringValue(cells.todo.write) ?? "",
    status_done_write_id: cellStringValue(cells.done.write) ?? "",
    status_discarded_write_id: cellStringValue(cells.discarded.write) ?? "",
  };
}

function stringArrayParam(
  parameters: Record<string, unknown>,
  key: string
): string[] | undefined {
  const encoded = getStringCellParam(parameters, key);
  if (encoded === undefined) return undefined;
  try {
    const parsed: unknown = JSON.parse(encoded);
    return Array.isArray(parsed) &&
      parsed.every((value) => typeof value === "string")
      ? parsed
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Reads a status mapping back out of the stored data-collection parameters.
 *
 * Provider-agnostic: the six `status_*` parameters are written by
 * {@link statusMappingParameters} for any integration whose completion column
 * holds provider ids (Notion option ids, Jira status ids).
 */
function storedStatusCells(
  parameters: Record<string, unknown>
): StatusCellMapping | undefined {
  const todo = stringArrayParam(parameters, "status_todo_ids");
  const done = stringArrayParam(parameters, "status_done_ids");
  const discarded = stringArrayParam(parameters, "status_discarded_ids");
  const todoWrite = getStringCellParam(parameters, "status_todo_write_id");
  const doneWrite = getStringCellParam(parameters, "status_done_write_id");
  const discardedWrite = getStringCellParam(
    parameters,
    "status_discarded_write_id"
  );
  if (
    !todo ||
    !done ||
    !discarded ||
    !todoWrite ||
    !doneWrite ||
    !discardedWrite
  ) {
    return undefined;
  }
  return {
    todo: { read: todo.map(stringCell), write: stringCell(todoWrite) },
    done: { read: done.map(stringCell), write: stringCell(doneWrite) },
    discarded: {
      read: discarded.map(stringCell),
      write: stringCell(discardedWrite),
    },
  };
}

/**
 * Resolves Routine's status cells from the synced Notion database's schema.
 *
 * Status option IDs belong to one specific database, so unlike Todoist's
 * `checked` booleans they cannot be written down in this file. This mirrors the
 * discontinued native integration, which read the database schema and
 * classified options by group (`fetch.ml:refine_notion_database`).
 */
async function resolveNotionStatusCells(
  connId: string,
  parameters: Record<string, unknown>,
  membrane: MembraneClient
): Promise<StatusCellResolution> {
  const stored = storedStatusCells(parameters);
  if (stored) return { kind: "resolved", cells: stored };
  // No database selected: there is nothing to look up, now or later.
  const databaseId = getStringCellParam(parameters, "database_id");
  if (!databaseId) return { kind: "unavailable" };

  // `findById` maps to GET /v1/databases/{id}
  // (databases/find-by-id.rest.yml): one request for the one database we
  // already know. `list()` maps to POST /v1/search, a cursor-paginated sweep of
  // every database the integration can see, narrowed to this id client-side.
  const response = await membrane
    .connection(connId)
    .dataCollection("databases")
    .findById({ id: databaseId });

  const record = property(response, "record");
  // The collection's recordFromApi mapping promotes the raw Notion database
  // under `fields`; read `properties` off the record itself as a second chance,
  // so the lookup holds whether or not that mapping runs for findById. Missing
  // it would leave the binding with no status mapping at all, and silently:
  // Notion sets requireResolvedStatusCells, so there is no fallback to notice.
  const properties =
    property(property(record, "fields"), "properties") ??
    property(record, "properties");
  // Every Notion database schema carries `properties`, so its absence says
  // something went wrong with the response rather than with the database:
  // report it as transient and let the retry decide.
  if (!isRecord(properties)) {
    return {
      kind: "failed",
      error: new Error(
        `Notion database ${databaseId} returned no properties map`
      ),
    };
  }

  const cellsOf = (
    candidates: Record<string, unknown>
  ): StatusCellResolution => {
    const cells =
      notionStatusPropertyCells(candidates) ?? notionCheckboxCells(candidates);
    // The schema parsed and holds no status, select or checkbox property we can
    // complete through. Terminal: only a change in Notion can fix it.
    return cells ? { kind: "resolved", cells } : { kind: "unavailable" };
  };

  const selectedPropertyId = getStringCellParam(
    parameters,
    "status_property_id"
  );
  if (selectedPropertyId) {
    const selected = Object.values(properties).find(
      (candidate) =>
        optionalStringProperty(candidate, "id") === selectedPropertyId
    );
    if (isRecord(selected)) return cellsOf({ selected });
  }
  return cellsOf(properties);
}

export const notionConfig: IntegrationSyncConfig = {
  integrationKey: "notion",
  label: "Notion",
  description:
    "Connect your Notion workspace to get your database items as tasks in Routine.",
  collectionKey: "database-items",

  // The type requires these to exist, but the UI ignores them because of
  // `requiresFilter: true` just below.
  filterOptionA: { icon: "📑", label: "All accessible items" },
  filterOptionB: { icon: "🗄️", label: "Specific database" },

  // Skips the A/B choice and requires the user to pick a database from the
  // dropdown instead.
  requiresFilter: true,

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    try {
      const response = await membrane
        .connection(connId)
        .dataCollection("databases")
        .list();

      const items: FilterItem[] = [];
      for (const record of arrayProperty(response, "records") ?? []) {
        const id = optionalStringProperty(record, "id");
        if (!id) continue;
        const fields = property(record, "fields");
        const title = property(fields, "title") ?? property(fields, "name");
        items.push({ id, name: notionPlainText(title) ?? id });
      }
      return items;
    } catch {
      // The picker shows "no databases" rather than breaking the wizard.
      return [];
    }
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "notion"),

  // `requiresFilter` is true, so the user always supplies a filterId (the
  // database).
  getParameters: (_choice, filterId, mapping): Record<string, string> => {
    return filterId
      ? {
          database_id: filterId,
          ...(mapping?.datePropertyId
            ? { date_property_id: mapping.datePropertyId }
            : {}),
          ...(mapping ? statusMappingParameters(mapping) : {}),
        }
      : {};
  },

  getChoiceFromParameters: (parameters) => {
    const databaseId = getStringCellParam(parameters, "database_id");
    // Report `optionB`, the branch that stands for a specific item.
    return { choice: "optionB", filterId: databaseId ?? "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Notion", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const dbId = getStringCellParam(parameters, "database_id");
    return dbId
      ? `${filterItemName(filterItems, dbId)} items`
      : "Database items";
  },

  getHint: (parameters, filterItems, storedName) => {
    const dbId = getStringCellParam(parameters, "database_id");
    if (!dbId) {
      return "Select a Notion database to sync items to your Routine inbox.";
    }
    const liveName = filterItems.find((p) => p.id === dbId)?.name;
    return `Items in database "${liveName ?? storedName ?? dbId}" will automatically appear in your Routine inbox.`;
  },

  // `routine_status` holds Notion option ids (or the connector's "true"/"false"
  // strings for a checkbox), so there is no valid boolean form of this mapping:
  // requireResolvedStatusCells stops a database with no promotable property
  // from silently binding `true`/`false` to a text column Notion never reads.
  fields: {
    url: "url",
    scheduled: "routine_date",
    completed: "routine_status",
    resolveStatusCells: resolveNotionStatusCells,
    requireResolvedStatusCells: true,
  },

  taskMapping: {
    fetchProperties: async (connId, databaseId, membrane) => {
      const response = await membrane
        .connection(connId)
        .dataCollection("databases")
        .list();
      return notionTaskMappingProperties(response, databaseId);
    },
    defaultMapping: defaultNotionTaskMapping,
  },

  completionActions: [
    {
      label: "Mark as done in Notion",
      fields: {
        url: "url",
        scheduled: "routine_date",
        completed: "routine_status",
        resolveStatusCells: resolveNotionStatusCells,
        requireResolvedStatusCells: true,
      },
    },
  ],

  illustrationRows: [
    { name: "Write Q3 OKRs", project: "Product Planning", completed: true },
    {
      name: "Review user interview notes",
      project: "Research",
      highlight: true,
    },
    { name: "Draft marketing copy", project: "Content Pipeline" },
    { name: "Update API documentation", project: "Engineering Docs" },
    { name: "Prepare all-hands slides", project: "Company DB" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

function mondayColumnSettings(settingsStr: string | null | undefined): unknown {
  if (!settingsStr) return undefined;
  try {
    const parsed: unknown = JSON.parse(settingsStr);
    return parsed;
  } catch {
    return undefined;
  }
}

function mondayStatusOptions(
  settings: unknown
): TaskSyncMappingProperty["options"] {
  if (!isRecord(settings)) return [];
  const done = new Set<string>();
  if (Array.isArray(settings.done_colors)) {
    for (const color of settings.done_colors) {
      if (typeof color === "number" || typeof color === "string") {
        done.add(String(color));
      }
    }
  }
  const labels = settings.labels;
  const options: TaskSyncMappingProperty["options"] = [];
  const push = (id: string, name: string) => {
    const group =
      done.has(id) || name.toLowerCase() === "done" ? "complete" : undefined;
    options.push({ id, name, ...(group ? { group } : {}) });
  };
  if (isRecord(labels)) {
    for (const [id, label] of Object.entries(labels)) {
      if (typeof label === "string" && label) push(id, label);
    }
  } else if (Array.isArray(labels)) {
    for (const entry of labels) {
      if (!isRecord(entry)) continue;
      const id =
        optionalStringProperty(entry, "id") ??
        (typeof entry.id === "number" ? String(entry.id) : undefined);
      const name =
        optionalStringProperty(entry, "label") ??
        optionalStringProperty(entry, "name");
      if (id && name) push(id, name);
    }
  }
  return options;
}

export function mondayTaskMappingProperties(
  response: unknown,
  boardId: string
): TaskSyncMappingProperty[] {
  const listed = arrayProperty(response, "records");
  const record = listed
    ? listed.find(
        (candidate) => optionalStringProperty(candidate, "id") === boardId
      )
    : property(response, "record");
  const fields = property(record, "fields");
  const columns =
    arrayProperty(fields, "columns") ?? arrayProperty(record, "columns");
  if (!columns) return [];

  const result: TaskSyncMappingProperty[] = [];
  for (const column of columns) {
    if (!isRecord(column)) continue;
    const id = optionalStringProperty(column, "id");
    const name = optionalStringProperty(column, "title");
    const type = optionalStringProperty(column, "type");
    if (!id || !name || !type) continue;
    if (type === "status" || type === "color") {
      result.push({
        id,
        name,
        kind: "status",
        options: mondayStatusOptions(
          mondayColumnSettings(optionalStringProperty(column, "settings_str"))
        ),
      });
    } else if (type === "checkbox" || type === "boolean") {
      result.push({ id, name, kind: "checkbox", options: [] });
    } else if (type === "date") {
      result.push({ id, name, kind: "date", options: [] });
    }
  }
  return result.sort((a, b) => a.name.localeCompare(b.name));
}

async function resolveMondayStatusCells(
  connId: string,
  parameters: Record<string, unknown>,
  membrane: MembraneClient
): Promise<StatusCellResolution> {
  const stored = storedStatusCells(parameters);
  if (stored) return { kind: "resolved", cells: stored };
  const boardId = getStringCellParam(parameters, "boardId");
  const statusPropertyId = getStringCellParam(parameters, "status_property_id");
  if (!boardId || !statusPropertyId) return { kind: "unavailable" };

  const response = await membrane
    .connection(connId)
    .dataCollection("boards")
    .findById({ id: boardId });
  const selected = mondayTaskMappingProperties(response, boardId).find(
    (candidate) => candidate.id === statusPropertyId
  );
  const cells = selected
    ? defaultNotionTaskMapping([selected]).statusCells
    : undefined;
  return cells ? { kind: "resolved", cells } : { kind: "unavailable" };
}

export const mondayConfig: IntegrationSyncConfig = {
  integrationKey: "monday",
  label: "Monday",
  description:
    "Connect your Monday account to get items from a board as tasks in Routine.",
  collectionKey: "items",
  configureQuestion: "Which board should sync items to Routine?",

  filterOptionA: { icon: "📋", label: "All boards" },
  filterOptionB: { icon: "🗂️", label: "Specific board" },
  requiresFilter: true,

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    try {
      const response = await membrane
        .connection(connId)
        .dataCollection("boards")
        .list();
      return getDataCollectionItems(response, ["name"]);
    } catch {
      return [];
    }
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "monday"),

  getParameters: (_choice, filterId, mapping): Record<string, string> => {
    return filterId
      ? {
          boardId: filterId,
          ...(mapping?.datePropertyId
            ? { date_property_id: mapping.datePropertyId }
            : {}),
          ...(mapping ? statusMappingParameters(mapping) : {}),
        }
      : {};
  },

  getChoiceFromParameters: (parameters) => {
    const boardId = getStringCellParam(parameters, "boardId");
    return { choice: "optionB", filterId: boardId ?? "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Monday", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const boardId = getStringCellParam(parameters, "boardId");
    return boardId
      ? `${filterItemName(filterItems, boardId)} items`
      : "Board items";
  },

  getHint: (parameters, filterItems, storedName) => {
    const boardId = getStringCellParam(parameters, "boardId");
    if (!boardId) {
      return "Select a Monday board to sync items to your Routine inbox.";
    }
    const liveName = filterItems.find((item) => item.id === boardId)?.name;
    return `Items on board "${liveName ?? storedName ?? boardId}" will automatically appear in your Routine inbox.`;
  },

  fields: {
    url: "url",
    scheduled: "routine_date",
    completed: "routine_status",
    resolveStatusCells: resolveMondayStatusCells,
    requireResolvedStatusCells: true,
  },

  taskMapping: {
    fetchProperties: async (connId, boardId, membrane) => {
      const response = await membrane
        .connection(connId)
        .dataCollection("boards")
        .findById({ id: boardId });
      return mondayTaskMappingProperties(response, boardId);
    },
    defaultMapping: defaultNotionTaskMapping,
  },

  completionActions: [
    {
      label: "Complete the item in Monday",
      fields: {
        url: "url",
        scheduled: "routine_date",
        completed: "routine_status",
        resolveStatusCells: resolveMondayStatusCells,
        requireResolvedStatusCells: true,
      },
    },
  ],

  illustrationRows: [
    { name: "Ship the landing page", project: "Launch", completed: true },
    { name: "Review the design", project: "Launch", highlight: true },
    { name: "Write the release notes", project: "Marketing" },
    { name: "Update the status column", project: "Operations" },
    { name: "Prepare the Monday board", project: "Planning" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── Outlook ──────────────────────────────────────────────────────────────────

export const outlookConfig: IntegrationSyncConfig = {
  integrationKey: "microsoft-outlook",
  label: "Outlook",
  description:
    "Connect your Outlook account to get your emails as tasks in Routine.",
  collectionKey: "messages",
  configureQuestion: "Which emails should become tasks?",

  filterOptionA: { icon: "📁", label: "Specific folder" },
  filterOptionB: { icon: "🏷️", label: "Specific category" },

  fetchFilterItemsA: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("mailfolders")
      .list();
    return getDataCollectionItems(response, ["displayName", "name"]);
  },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .proxy.get("/me/outlook/masterCategories");
    return getOutlookCategories(response);
  },

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "microsoft-outlook"),

  getParameters: (choice, filterId): Record<string, string> =>
    choice === "optionA"
      ? { mailFolderId: filterId }
      : { categoryName: filterId },

  getChoiceFromParameters: (parameters) => {
    const mailFolderId = getStringCellParam(parameters, "mailFolderId");
    if (mailFolderId) return { choice: "optionA", filterId: mailFolderId };
    const categoryName =
      getStringCellParam(parameters, "categoryName") ??
      getStringCellParam(parameters, "category");
    if (categoryName) return { choice: "optionB", filterId: categoryName };
    return { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Microsoft Outlook", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const folderId = getStringCellParam(parameters, "mailFolderId");
    const category =
      getStringCellParam(parameters, "categoryName") ??
      getStringCellParam(parameters, "category");
    return folderId
      ? `"${filterItemName(filterItems, folderId)}" folder emails`
      : category
        ? `"${category}" category emails`
        : "Emails";
  },

  getHint: (parameters, filterItems) => {
    const folderId = getStringCellParam(parameters, "mailFolderId");
    if (folderId) {
      const liveName = filterItems.find((f) => f.id === folderId)?.name;
      return `Emails in folder "${liveName ?? folderId}" will automatically become tasks in your Routine inbox.`;
    }
    const category =
      getStringCellParam(parameters, "categoryName") ??
      getStringCellParam(parameters, "category");
    if (category) {
      return `Emails categorized as "${category}" will automatically become tasks in your Routine inbox.`;
    }
    return "Your Outlook emails will automatically become tasks in your Routine inbox.";
  },

  fields: { url: "webLink", completed: "isRead" },

  completionActions: [
    {
      label: "Mark as read",
      fields: { url: "webLink", completed: "isRead" },
    },
  ],

  IllustrationComponent: markRaw(OutlookIllustration),
};

// ── Featurebase ──────────────────────────────────────────────────────────────

/**
 * Featurebase post statuses are per-organization: a status id is an ObjectId
 * belonging to one workspace, so — exactly like Notion's status option ids —
 * the values cannot be written down in this file. What *is* stable is each
 * status's `type`, one of Featurebase's five fixed workflow stages, so the
 * mapping keys off that and carries the ids it discovers.
 */
const isFeaturebaseDone = (type: string | undefined): boolean =>
  type === "completed";

const isFeaturebaseDiscarded = (type: string | undefined): boolean =>
  type === "canceled";

export type FeaturebasePostStatus = {
  id: string;
  type: string | undefined;
  isDefault: boolean;
};

/** Parses the `post-statuses` data collection records, skipping malformed ones. */
export function featurebasePostStatuses(
  response: unknown
): FeaturebasePostStatus[] {
  const statuses: FeaturebasePostStatus[] = [];
  for (const record of arrayProperty(response, "records") ?? []) {
    const id = optionalStringProperty(record, "id");
    if (!id) continue;
    const fields = property(record, "fields");
    statuses.push({
      id,
      type: optionalStringProperty(fields, "type") ?? undefined,
      isDefault: property(fields, "isDefault") === true,
    });
  }
  return statuses;
}

/**
 * Classifies a workspace's post statuses into Routine's three task states.
 *
 * Returns `undefined` when the workspace has no completed status or no open
 * one, leaving `resolveStatusCellMapping` to fall back rather than binding a
 * mapping that could never complete (or never reopen) a post.
 */
export function featurebaseStatusCells(
  statuses: FeaturebasePostStatus[]
): StatusCellMapping | undefined {
  const done = statuses.filter((status) => isFeaturebaseDone(status.type));
  const discarded = statuses.filter((status) =>
    isFeaturebaseDiscarded(status.type)
  );
  // Everything that is neither completed nor canceled counts as still open,
  // including `reviewing` and any stage Featurebase adds later. A status that
  // matched no read set would leave its posts showing as neither done nor todo.
  const todo = statuses.filter(
    (status) =>
      !isFeaturebaseDone(status.type) && !isFeaturebaseDiscarded(status.type)
  );

  const doneWrite = done[0]?.id;
  // Prefer the board's default status for the reopen write: the first open
  // status may well be a `reviewing` one, which would hide the post from the
  // roadmap instead of putting it back in progress.
  const todoWrite = (todo.find((status) => status.isDefault) ?? todo[0])?.id;
  if (!doneWrite || !todoWrite) return undefined;

  return {
    done: {
      read: done.map((status) => stringCell(status.id)),
      write: stringCell(doneWrite),
    },
    todo: {
      read: todo.map((status) => stringCell(status.id)),
      write: stringCell(todoWrite),
    },
    discarded: {
      read: discarded.map((status) => stringCell(status.id)),
      // Not every workspace keeps a canceled status, so fall back to the
      // completed one: discarding in Routine should still close the post.
      write: stringCell(discarded[0]?.id ?? doneWrite),
    },
  };
}

async function resolveFeaturebaseStatusCells(
  connId: string,
  _parameters: Record<string, unknown>,
  membrane: MembraneClient
): Promise<StatusCellResolution> {
  const response = await membrane
    .connection(connId)
    .dataCollection("post-statuses")
    .list();
  const cells = featurebaseStatusCells(featurebasePostStatuses(response));
  // The workspace answered and none of its statuses classify into done/todo.
  // Terminal: only a change in Featurebase can fix it. A request that never
  // got that far throws instead, which the caller retries.
  return cells ? { kind: "resolved", cells } : { kind: "unavailable" };
}

export const featurebaseConfig: IntegrationSyncConfig = {
  integrationKey: "featurebase",
  label: "Featurebase",
  description:
    "Connect your Featurebase workspace to get feedback posts as tasks in Routine.",
  collectionKey: "posts",

  configureQuestion: "Which posts should become tasks?",
  filterOptionA: { icon: "📥", label: "All posts" },
  filterOptionB: { icon: "🗂", label: "Specific board" },

  fetchFilterItems: async (connId: string, membrane: MembraneClient) => {
    const response: unknown = await membrane
      .connection(connId)
      .dataCollection("boards")
      .list();
    return getDataCollectionItems(response, ["name"]);
  },

  // No fetchConnectionLabel: a Featurebase API key authenticates a whole
  // workspace rather than a person, so there is no per-account label to show.

  getParameters: (choice, filterId): Record<string, string> =>
    choice === "optionB" && filterId ? { boardId: filterId } : {},

  getChoiceFromParameters: (parameters) => {
    const boardId = getStringCellParam(parameters, "boardId");
    return boardId
      ? { choice: "optionB", filterId: boardId }
      : { choice: "optionA", filterId: "" };
  },

  getDisplayName: (_choice, filterName, connectionLabel) =>
    getGenericDisplayName("Featurebase", connectionLabel, filterName),

  getSyncName: (parameters, filterItems) => {
    const boardId = getStringCellParam(parameters, "boardId");
    return boardId
      ? `${filterItemName(filterItems, boardId)} posts`
      : "All posts";
  },

  getHint: (parameters, filterItems, storedName) => {
    const boardId = getStringCellParam(parameters, "boardId");
    if (!boardId) {
      return "All posts in your Featurebase workspace will automatically appear in your Routine inbox.";
    }
    const liveName = filterItems.find((board) => board.id === boardId)?.name;
    return `Posts on the "${liveName ?? storedName ?? boardId}" board will automatically appear in your Routine inbox.`;
  },

  // `scheduled` is deliberately left unmapped. Featurebase's `eta` is a public
  // roadmap commitment, so planning a post in your day must not write a date
  // back to it: Routine scheduling stays personal, and the post's ETA is only
  // ever changed in Featurebase.
  // `statusId` holds Featurebase status ids, so there is no valid boolean form
  // of this mapping: requireResolvedStatusCells stops a failed lookup from
  // silently binding `true`/`false` to a string column.
  fields: {
    url: "postUrl",
    completed: "statusId",
    resolveStatusCells: resolveFeaturebaseStatusCells,
    requireResolvedStatusCells: true,
  },

  completionActions: [
    {
      label: "Mark the post as complete in Featurebase",
      fields: {
        url: "postUrl",
        completed: "statusId",
        resolveStatusCells: resolveFeaturebaseStatusCells,
        requireResolvedStatusCells: true,
      },
    },
  ],

  illustrationRows: [
    {
      name: "Dark mode for the mobile app",
      project: "Feature Requests",
      completed: true,
    },
    {
      name: "Calendar sync drops events",
      project: "Bug Reports",
      highlight: true,
    },
    { name: "Bulk edit tasks", project: "Feature Requests" },
    { name: "Keyboard shortcut for search", project: "Feature Requests" },
    { name: "Export to CSV", project: "Feature Requests" },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};

// ── WhatsApp ─────────────────────────────────────────────────────────────────

/**
 * Product semantics:
 * - Scope: all inbound messages to the connected WhatsApp Business number
 *   become tasks (optional Membrane `from` parameter reserved for a future
 *   contact filter).
 * - Complete: mark as read (`isRead`), same pattern as Outlook email tasks.
 * - Sync: webhook-only — Meta has no list-messages API; the connector omits
 *   list and Routine skips wiping No_list_method collections on refresh.
 */
export const whatsappConfig: IntegrationSyncConfig = {
  integrationKey: "whatsapp",
  label: "WhatsApp",
  description:
    "Connect your WhatsApp Business account to get inbound messages as tasks in Routine.",
  collectionKey: "messages",
  configureQuestion: "Which WhatsApp messages should become tasks?",

  filterOptionA: { icon: "💬", label: "All inbound messages" },
  // WhatsApp Cloud API has no contacts list; option B reuses the same scope
  // until a contact picker can be backed by stored webhook senders. A single
  // sentinel item keeps the option-B picker submittable.
  filterOptionB: { icon: "💬", label: "All inbound messages" },

  fetchFilterItems: async () => [{ id: "_all", name: "All inbound messages" }],

  fetchConnectionLabel: (connId: string, membrane: MembraneClient) =>
    fetchLabelForConnection(membrane, connId, "whatsapp"),

  getParameters: () => ({}),

  getChoiceFromParameters: () => ({ choice: "optionA", filterId: "" }),

  getDisplayName: (_choice, _filterName, connectionLabel) =>
    getGenericDisplayName("WhatsApp", connectionLabel),

  getSyncName: () => "Inbound messages",

  getHint: () =>
    "Inbound WhatsApp messages will automatically become tasks in your Routine inbox. Completing a task marks the message as read.",

  fields: { url: "url", completed: "isRead" },

  completionActions: [
    {
      label: "Mark as read",
      fields: { url: "url", completed: "isRead" },
    },
  ],

  illustrationRows: [
    { name: "Can you send the invoice?", project: "WhatsApp", highlight: true },
    { name: "Meeting moved to 3pm", project: "WhatsApp" },
    { name: "Thanks!", project: "WhatsApp", completed: true },
  ],

  IllustrationComponent: markRaw(TaskListIllustration),
};
